import { IconButton, Link, MenuItem, MenuList, SvgIconProps, validateUrlProtocol } from '@chronosphereio/chrono-ui';
// This is a temporary override for an old use case until we complete the Chrono UI Popover backfill (sc-138310)
// eslint-disable-next-line no-restricted-imports
import { Popover } from '@mui/material';
import { Link as LinkIcon, DotsVertical as ThreeDotMenu } from 'mdi-material-ui';
import * as React from 'react';
import { isHrefSeriesMenuItem, SeriesMenuItemOptions } from '@/core';
import { SeriesInfoContext, TooltipLink } from './tooltip-model';

type SeriesTooltipLinksProps = {
  seriesInfo: SeriesInfoContext;
  /**
   * Context links defined in the panel definition.
   */
  links?: TooltipLink[];
  /**
   * Actions specific to the series.
   * These are shown as menu items in the tooltip
   */
  seriesActions?: SeriesMenuItemOptions[];
  onClick?: () => void;
};

export type SeriesAction = {
  icon: React.ElementType<SvgIconProps>;
  title: string;
  description?: string;
  onClick: () => void;
};

const DEFAULT_TOOLTIP_LINK: TooltipLink[] = [];
const DEFAULT_SERIES_ACTIONS: SeriesMenuItemOptions[] = [];
// Needed to ensure the zIndex is higher than the tooltip
const Z_INDEX_TOOLTIP_POPOVER = 2000;

export function SeriesTooltipActions({
  seriesInfo,
  links = DEFAULT_TOOLTIP_LINK,
  seriesActions = DEFAULT_SERIES_ACTIONS,
  onClick = () => {},
}: SeriesTooltipLinksProps) {
  const [menuAnchorEl, setMenuAnchorEl] = React.useState<HTMLButtonElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    // stop event from bubbling up to parent so tooltip doesn't close
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setMenuAnchorEl(null);
  };

  const open = Boolean(menuAnchorEl);
  const id = open ? 'series-links' : undefined;

  if ((!links || links?.length === 0) && seriesActions.length === 0) {
    return null;
  }

  return (
    <>
      <IconButton size="small" onClick={handleClick} sx={{ padding: '1px' }}>
        <ThreeDotMenu />
      </IconButton>
      <Popover
        id={id}
        open={open}
        anchorEl={menuAnchorEl}
        onClose={handleClose}
        sx={{ zIndex: 2000 }}
        PaperProps={{
          sx: (theme) => ({
            backgroundColor: theme.palette.background.popover,
            boxShadow: theme.shadows[5],
            borderRadius: '4px',
            backgroundImage: 'none', // remove the default linear-gradiant from mui
            zIndex: Z_INDEX_TOOLTIP_POPOVER,
          }),
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <MenuList>
          {seriesActions.map((action) => {
            if (isHrefSeriesMenuItem(action)) {
              return (
                <Link
                  sx={{ color: 'inherit' }}
                  href={action.href}
                  target={action.target}
                  rel="noreferrer"
                  key={action.title}
                >
                  <MenuItem>
                    <action.icon sx={(t) => ({ fontSize: t.typography.body1.fontSize, mr: 1 })} />
                    {action.title}
                  </MenuItem>
                </Link>
              );
            }
            return (
              <MenuItem
                key={action.title}
                onClick={() => {
                  action.onClick();
                  onClick();
                }}
              >
                {<action.icon sx={(t) => ({ fontSize: t.typography.body1.fontSize, mr: 1 })} />}
                {action.title}
              </MenuItem>
            );
          })}
          {links.map((link) => {
            const safeHref = validateUrlProtocol(link.buildUrl(seriesInfo));

            // If the URL's protocol is not in the allowlist (e.g. javascript:,
            // data:), render the menu item without an anchor so the disallowed
            // scheme never reaches the DOM.
            if (safeHref === null) {
              return (
                <MenuItem key={link.title} disabled>
                  <LinkIcon sx={(t) => ({ fontSize: t.typography.body1.fontSize, mr: 1 })} />
                  {link.title}
                </MenuItem>
              );
            }

            return (
              <Link
                sx={{ color: 'inherit' }}
                href={safeHref}
                target="_blank"
                rel="noreferrer"
                key={link.title}
                onClick={onClick}
              >
                <MenuItem>
                  <LinkIcon sx={(t) => ({ fontSize: t.typography.body1.fontSize, mr: 1 })} />
                  {link.title}
                </MenuItem>
              </Link>
            );
          })}
        </MenuList>
      </Popover>
    </>
  );
}
